/**
 * web/src/components/BonusEditModal.tsx
 *
 * 賞与登録・編集モーダル（管理者専用）
 * Payroll.tsx の PayrollEditModal と同じ考え方の共通コンポーネント。
 * Personnel.tsx（支払準備タブの行内「＋賞与を追加」）と
 * Payroll.tsx（賞与一覧タブ）の両方から呼び出される。
 */

import { useState } from 'react'
import { api } from '../api/client'
import type { BonusRecord } from './BonusSlipPreview'

export const BONUS_STATUS_LABELS: Record<string, string> = {
  draft: '下書き', confirmed: '確定', paid: '支払済', canceled: 'キャンセル',
}

function fmt(n: number | null | undefined) { return (n ?? 0).toLocaleString('ja-JP') }

export type BonusEditRecord = Partial<BonusRecord> & { assistant_id: string }

type Props = {
  initial: BonusEditRecord | null
  assistants: any[]
  onClose: () => void
  onSaved: () => void
}

export function BonusEditModal({ initial, assistants, onClose, onSaved }: Props) {
  const isNew = !initial?.id
  const [form, setForm] = useState<Record<string, any>>(() => ({
    assistant_id: '', bonus_name: '', payment_date: '',
    gross_amount: 0, health_insurance: 0, pension: 0, employment_insurance: 0,
    other_social_insurance: 0, income_tax: 0, other_deductions: 0,
    remarks: '', status: 'draft', issuer_name: 'スタジオねこのみや／浪川周',
    ...initial,
  }))
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const n = (k: string) => Number(form[k] ?? 0)
  const set = (k: string, v: unknown) => setForm((f: Record<string, any>) => ({ ...f, [k]: v }))

  // 自動計算（プレビュー）
  const deductions = n('health_insurance') + n('pension') + n('employment_insurance') +
                      n('other_social_insurance') + n('income_tax') + n('other_deductions')
  const netAmount = n('gross_amount') - deductions

  const handleSave = async () => {
    if (!form.assistant_id || !form.bonus_name || !form.payment_date) {
      setErr('アシスタント・賞与名・支給日は必須です')
      return
    }
    setSaving(true)
    setErr(null)
    try {
      const payload = { ...form, deductions_total: deductions, net_amount: netAmount }
      if (isNew) {
        await api.bonuses.create(payload)
      } else {
        await api.bonuses.update(initial!.id!, payload)
      }
      onSaved()
      onClose()
    } catch (e: any) {
      setErr(e.message ?? '保存に失敗しました')
    } finally {
      setSaving(false)
    }
  }

  const AmountField = ({ label, k }: { label: string; k: string }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <label style={{ fontSize: 10, color: 'var(--ink-3)', fontWeight: 600 }}>{label}</label>
      <input type="number" value={form[k] ?? 0} min={0}
        onChange={e => set(k, Number(e.target.value))}
        style={{ fontFamily: 'var(--font-mono)', textAlign: 'right', fontSize: 13 }}
      />
    </div>
  )

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 560, maxHeight: '92vh', overflowY: 'auto' }}>
        <div className="modal-header">
          <div className="modal-title">{isNew ? '賞与 新規登録' : '賞与 編集'}</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {err && <div style={{ color: 'var(--red)', fontSize: 12, marginBottom: 12, padding: '8px 12px', background: 'var(--red-light)', borderRadius: 6 }}>{err}</div>}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10, marginBottom: 12 }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label>アシスタント *</label>
              <select value={form.assistant_id} onChange={e => set('assistant_id', e.target.value)} disabled={!isNew}>
                <option value="">選択してください</option>
                {assistants.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label>賞与名 *</label>
                <input type="text" value={form.bonus_name} placeholder="例: 2026年夏季賞与"
                  onChange={e => set('bonus_name', e.target.value)} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label>支給日 *</label>
                <input type="date" value={form.payment_date ?? ''} onChange={e => set('payment_date', e.target.value)} />
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
            <div style={{ gridColumn: '1/-1', fontWeight: 700, fontSize: 11, color: 'var(--ink-2)',
              textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid var(--paper-2)',
              paddingBottom: 4 }}>
              支給・控除
            </div>
            <AmountField label="賞与総額" k="gross_amount" />
            <AmountField label="健康保険" k="health_insurance" />
            <AmountField label="厚生年金" k="pension" />
            <AmountField label="雇用保険" k="employment_insurance" />
            <AmountField label="その他社会保険" k="other_social_insurance" />
            <AmountField label="所得税" k="income_tax" />
            <AmountField label="その他控除" k="other_deductions" />
          </div>

          {/* 自動計算プレビュー */}
          <div style={{ marginTop: 10, display: 'flex', gap: 16, padding: '8px 12px',
            background: 'var(--paper-2)', borderRadius: 6, fontSize: 12 }}>
            <span>控除合計：<strong>¥{fmt(deductions)}</strong></span>
          </div>
          <div style={{ marginTop: 8, padding: '12px 16px', background: 'var(--green-light)',
            border: '1px solid rgba(45,122,58,0.25)', borderRadius: 8,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>差引支給額（自動計算）</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 22, fontWeight: 700, color: 'var(--green)' }}>
              ¥{fmt(netAmount)}
            </span>
          </div>

          <div style={{ marginTop: 12 }}>
            <div className="form-group">
              <label>備考</label>
              <textarea rows={3} value={form.remarks ?? ''}
                onChange={e => set('remarks', e.target.value)}
                style={{ resize: 'vertical', fontSize: 12 }} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label>発行者</label>
                <input type="text" value={form.issuer_name ?? ''} onChange={e => set('issuer_name', e.target.value)} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label>ステータス</label>
                <select value={form.status} onChange={e => set('status', e.target.value)}>
                  {Object.entries(BONUS_STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>キャンセル</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    </div>
  )
}
